import { useMemo, useState } from 'react'
import { Loader2, X } from 'lucide-react'
import type {
  KenntnisGroup,
  KenntnisPreviewResponse,
} from '@/services/api/timetableDataQuality'

const GROUP_LABELS: Record<KenntnisGroup, string> = {
  ignore: 'Ignore',
  subject: 'Fächer',
  room: 'Räume',
  class_name: 'Klassen',
  split: 'Splits',
}

export function KenntnisSealModal({
  preview,
  busy,
  onClose,
  onSeal,
}: {
  preview: KenntnisPreviewResponse
  busy: boolean
  onClose: () => void
  onSeal: (groups: KenntnisGroup[]) => void
}) {
  const available = useMemo(
    () =>
      (Object.keys(GROUP_LABELS) as KenntnisGroup[]).filter(
        (group) => (preview.delta[group] || []).length > 0,
      ),
    [preview],
  )
  const [selected, setSelected] = useState<Record<KenntnisGroup, boolean>>(() => ({
    ignore: true,
    subject: true,
    room: true,
    class_name: true,
    split: true,
  }))
  const chosen = available.filter((group) => selected[group])
  const deltaCount = available.reduce((sum, group) => sum + (preview.delta[group] || []).length, 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Kenntnis speichern</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-gray-700">
          <p>
            Token-Shop, Katalog und Stundenplan-Template werden aus Fächern, Räumen, Klassen, Ignore und Splits neu geschrieben.
          </p>
          {available.map((group) => {
            const items = preview.delta[group] || []
            return (
              <label key={group} className="flex items-start gap-2 rounded border border-gray-200 p-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected[group]}
                  onChange={(e) => setSelected((prev) => ({ ...prev, [group]: e.target.checked }))}
                />
                <span>
                  <span className="font-medium">
                    {GROUP_LABELS[group]} · {items.length}
                  </span>
                  <span className="mt-1 block font-mono text-xs text-gray-500">
                    {items.slice(0, 8).map((item) => item.token).join(', ')}
                    {items.length > 8 ? ' …' : ''}
                  </span>
                </span>
              </label>
            )
          })}
          <p className="text-xs text-gray-500">
            Aliase bereits aktiv: {preview.already_live.aliases} · Muster live:{' '}
            {preview.already_live.patterns}
            {preview.skipped_year_classes.length > 0
              ? ` · Jahr-Klassen nicht gespeichert: ${preview.skipped_year_classes.slice(0, 4).join(', ')}`
              : ''}
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={busy || chosen.length === 0 || deltaCount === 0}
            onClick={() => onSeal(chosen)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  )
}
